import pick from 'lodash/pick';
import { createAction } from 'redux-actions';

import { tapFlashError, tapFlashSuccess } from '../generic/flash-messages';
import { OPERATIVE_SYSTEM_TYPES } from '../products/constants';
import api from '../REST/api';
import createUiAction, { markUiAction } from '../utils/create-ui-action';
import { EDITOR_NAME } from './connect-editor';
import transformForPatch, {
  transformCampaignLineForPatch,
} from './transform-for-patch';
import transformForRendering, {
  combineLinesByBusinesses,
} from './transform-for-rendering';
import {
  getLineIdsUsingCollectionIds,
  getProductIdsUsingCollectionIds,
} from './utils/collection-id-utils';
import { getLineIdsUsingContentIds } from './utils/content-id-utils';
import {
  getPushLinks,
  getPushLinksDC,
} from './utils/downstream-system-links-utils';

export const changeCampaign = createAction(
  'campaign-editor/change-campaign',
  (name, value) => ({ name, value }),
  (name, value, isUiAction = false) => ({ uiAction: isUiAction }),
);

export const changeCampaignLine = createAction(
  'campaign-editor/change-campaign-line',
  (name, value, markDirty = !name.endsWith('.selected')) => ({
    name,
    value,
    markDirty,
  }),
  name => ({ uiAction: name.endsWith('.selected') }),
);

export const changeCampaignLines = createAction(
  'campaign-editor/change-campaign-lines',
  (ids, name, value, markDirty = name !== 'selected') => ({
    ids,
    name,
    value,
    markDirty,
  }),
  (_, name) => ({ uiAction: name === 'selected' }),
);

export const bulkEditLanguageValuesForCampaignLines = createAction(
  'campaign-editor/bulk-edit-language-values-for-campaign-lines',
  (ids, languages, name, value) => ({ ids, languages, name, value }),
);

export const bulkEditPartialLPUrlForCampaignLines = createAction(
  'campaign-editor/bulk-edit-partial-lp-url-for-campaign-lines',
  (ids, languages, name, value) => ({ ids, languages, name, value }),
);

export const applyTextBundle = createAction(
  'campaign-editor/apply-text-bundle',
  (campaignLineTextBundle, brand, selectedLineIds) => ({
    campaignLineTextBundle,
    brand,
    selectedLineIds,
  }),
);

export const removeLanguageLine = createAction(
  'campaign-editor/remove-campaign-language-line',
  (id, languageCode) => ({ id, languageCode }),
);

export const storeSavedCampaign = createAction(
  'campaign-editor/saved-campaign',
  savedCampaign => ({ savedCampaign }),
);

export const submit = initialCampaign => (dispatch, getState) => {
  const state = getState();
  const { campaign, campaignLinesById, savedCampaign } = state[EDITOR_NAME];
  // campaignToCompare is used to compared the initial campaign with
  // the current campaign changes, to send only the changed fields to BE.
  // After save, campaignToCompare will be the previous saved campaign
  // so the process can be repeated with an updated campaign instead.
  const campaignToCompare = savedCampaign || initialCampaign;
  return dispatch({
    type: 'campaign-editor/submit',
    payload: api
      .patchCampaign({
        id: campaign.id,
        data: transformForPatch(campaignToCompare, campaign, campaignLinesById),
      })
      .then(response => {
        dispatch(storeSavedCampaign(response));
        return transformForRendering(response, state.products, state.merchants);
      })
      .then(
        tapFlashSuccess(dispatch, 'Campaign update successful'),
        tapFlashError(dispatch),
      ),
    meta: { resetModifiedOnSuccess: true },
  });
};

submit.toString = () => 'campaign-editor/submit';

export const requestChange = () => (dispatch, getState) => {
  const state = getState();
  const { campaign, campaignLinesById } = state[EDITOR_NAME];
  return dispatch({
    type: 'campaign-editor/request-change',
    payload: api
      .addCampaignChange({
        id: campaign.id,
        data: transformForPatch(campaign, campaignLinesById),
      })
      .then(response =>
        transformForRendering(response, state.products, state.merchants),
      )
      .then(
        tapFlashSuccess(dispatch, 'Change Request Posted'),
        tapFlashError(dispatch),
      ),
  });
};

requestChange.toString = () => 'campaign-editor/request-change';

export const push =
  ({
    system,
    successMessage,
    errorMessage,
    productCode,
    isDraftCampaignEnabled,
  }) =>
  (dispatch, getState) => {
    const state = getState();
    const {
      campaign: { id, campaignLineIds },
      campaignLinesById,
    } = state[EDITOR_NAME];

    const isCCP = productCode ? productCode.includes('CCP') : false;

    let params;
    if (isDraftCampaignEnabled) {
      params = { id, system };
    } else {
      let systemValue;
      if (system === OPERATIVE_SYSTEM_TYPES.zmsAm.system && !isCCP) {
        systemValue = `${system}_campaign`;
      } else if (isCCP) {
        systemValue = `${system}_to_nakadi`;
      } else {
        systemValue = system;
      }
      params = { id, system: systemValue };
    }

    if (productCode) {
      params.campaignLineIds = campaignLineIds
        .map(lineId => campaignLinesById[lineId])
        .filter(line => line.product.code === productCode)
        .map(line => line.id);
    }

    return dispatch({
      type: 'campaign-editor/push',
      meta: { resetModifiedOnSuccess: true },
      payload: api
        .pushCampaign(params)
        .then(response =>
          transformForRendering(response, state.products, state.merchants),
        )
        .then(
          tapFlashSuccess(dispatch, successMessage),
          tapFlashError(dispatch, errorMessage),
        ),
    });
  };

push.toString = () => 'campaign-editor/push';

export const togglePushInProgress = createUiAction(
  'campaign-editor/toggle-push-in-progress',
  status => ({ status }),
);

export const fetchCampaignLineCreatives = () => (dispatch, getState) => {
  const { campaignEditor } = getState();
  if (!campaignEditor) return;

  const { campaignLineIds } = campaignEditor.campaign;
  dispatch(
    markUiAction({
      type: fetchCampaignLineCreatives.toString(),
      payload: api
        .getCampaignLineCreatives({
          // This is the only endpoint where array-arguments are serialized this way
          search: { campaignLineId: campaignLineIds.join(',') },
        })
        .then(data => ({ campaignLineCreatives: data })),
    }),
  );
};

fetchCampaignLineCreatives.toString = () =>
  'campaign-editor/fetch-campaign-line-creatives';

export const toggleCampaignLineExpansion = createUiAction(
  'campaign-editor/toggle-campaign-line-expansion',
  (label, expanded) => ({
    label,
    expanded,
  }),
);

export const toggleAllCampaignLinesExpansion = createUiAction(
  'campaign-editor/toggle-all-campaign-lines-expansion',
  expanded => ({ expanded }),
);

export const changeFilter = createUiAction(
  'campaign-editor/change-filter',
  (name, value) => ({ name, value }),
);

export const filterCampaignLines = createUiAction(
  'campaign-editor/filter-campaign-lines',
  selectedFilters => ({ selectedFilters }),
);

export const applyFilters = (name, value) => (dispatch, getState) => {
  // Apply selections to filters
  if (name && value) dispatch(changeFilter(name, value));

  // Mark lines as filtered out based on filter selections
  const { campaignEditor } = getState();
  if (!campaignEditor) return;

  const { campaignLineFilters } = campaignEditor;
  dispatch(filterCampaignLines(campaignLineFilters));

  // Fetching the campaign lines here to get the new updated
  // state from redux store
  const { campaignLinesById } = getState().campaignEditor;

  // Hide the filtered out lines
  const filteredLines = Object.values(campaignLinesById).filter(
    line => !line.filteredOut,
  );
  dispatch(
    changeCampaign(
      'campaignLines',
      combineLinesByBusinesses(filteredLines, true),
      true,
    ),
  );
};

applyFilters.toString = () => 'campaign-editor/apply-filters';

export const toggleCreativesVisibility = createUiAction(
  'campaign-editor/toggle-creatives-visibility',
  areCreativesVisible => ({ areCreativesVisible }),
);

export const toggleDelayReasonVisibility = createUiAction(
  'campaign-editor/toggle-delay-reason-visibility',
  isDelayReasonVisible => ({ isDelayReasonVisible }),
);

export const revealLine = id => (dispatch, getState) => {
  const { campaignLines } = getState().campaignEditor.campaign;
  campaignLines.forEach(businessLine => {
    const productWithTargetCampaignLine = businessLine.products.find(
      productLine =>
        productLine.countries.flatMap(ea => ea.lines).some(clId => id === clId),
    );

    if (productWithTargetCampaignLine) {
      dispatch(toggleCampaignLineExpansion(businessLine.label, true));
      dispatch(
        toggleCampaignLineExpansion(productWithTargetCampaignLine.label, true),
      );
    }
  });
};

revealLine.toString = () => 'campaign-editor/reveal-line';

export const initializeExpansionStateOfAllBusinessesAndProducts =
  createUiAction(
    'campaign-editor/initialize-expansion-state',
    campaignLines => ({ campaignLines }),
  );

export const changeCampaignLineStatus =
  (lineIdsToSave, status) => (dispatch, getState) => {
    const { campaign, campaignLinesById } = getState()[EDITOR_NAME];
    const campaignLines = campaign.campaignLineIds.map(id => {
      const line = campaignLinesById[id];
      return lineIdsToSave.includes(id)
        ? { ...transformCampaignLineForPatch(line), status }
        : pick(line, ['id']);
    });
    return dispatch({
      type: 'campaign-editor/change-campaign-line-status',
      payload: api
        .patchCampaign({
          id: campaign.id,
          data: { campaignLines },
        })
        .then(response => {
          dispatch(changeCampaignLines(lineIdsToSave, 'status', status, false));
          // OpsHandoverCompliance is updated for every campaign line passed after getting it from the response
          lineIdsToSave.forEach(id => {
            dispatch(
              changeCampaignLines(
                [id],
                'opsHandoverCompliance',
                response.campaignLines.find(item => item.id === id)
                  .opsHandoverCompliance,
                false,
              ),
            );
          });
          tapFlashSuccess(
            dispatch,
            `Campaign lines ${lineIdsToSave
              .map(id => `CL-${id}`)
              .join(', ')} updated`,
          )();
        }, tapFlashError(dispatch)),
      meta: { resetModifiedOnSuccess: true },
    });
  };

changeCampaignLineStatus.toString = () =>
  'campaign-editor/change-campaign-line-status';

export const setupCFLinks = () => (dispatch, getState) => {
  const { campaignLinesById, campaign } = getState()[EDITOR_NAME];

  return dispatch(
    markUiAction({
      type: 'campaign-editor/setup-downstream-system-links',
      payload: getPushLinksDC(campaign, campaignLinesById),
    }),
  );
};

setupCFLinks.toString = () => 'campaign-editor/setup-downstream-system-links';

// Delete when draft campaign flag is deleted
export const setupCFAndCCPLinks = () => (dispatch, getState) => {
  const { campaignLinesById, campaign } = getState()[EDITOR_NAME];

  return dispatch(
    markUiAction({
      type: 'campaign-editor/setup-downstream-system-links',
      payload: getPushLinks(campaign, campaignLinesById),
    }),
  );
};

setupCFAndCCPLinks.toString = () =>
  'campaign-editor/setup-downstream-system-links';

export const clearTextAssetsForProductsUsingCollectionIds =
  () => (dispatch, getState) => {
    const { campaignLinesById, campaign } = getState()[EDITOR_NAME];
    const lineIdsToClean = getLineIdsUsingCollectionIds(
      campaign,
      campaignLinesById,
    );
    return dispatch({
      type: clearTextAssetsForProductsUsingCollectionIds.toString(),
      payload: { lineIdsToClean },
    });
  };

clearTextAssetsForProductsUsingCollectionIds.toString = () =>
  'campaign-editor/clear-text-assets-for-products-using-collection-ids';

export const clearTextAssetsForProductsUsingContentIds =
  () => (dispatch, getState) => {
    const { campaignLinesById, campaign } = getState()[EDITOR_NAME];
    const lineIdsToClean = getLineIdsUsingContentIds(
      campaign,
      campaignLinesById,
    );
    return dispatch({
      type: clearTextAssetsForProductsUsingContentIds.toString(),
      payload: { lineIdsToClean },
    });
  };

clearTextAssetsForProductsUsingContentIds.toString = () =>
  'campaign-editor/clear-text-assets-for-products-using-content-ids';

export const clearHighlightedSKUsForProductsUsingCollectionIds =
  () => (dispatch, getState) => {
    const {
      [EDITOR_NAME]: { campaign },
      products,
    } = getState();
    const productIdsToClean = getProductIdsUsingCollectionIds(campaign);
    const productCodesToClean = productIdsToClean.map(
      id => products.find(p => p.id === id).code,
    );
    return dispatch({
      type: clearHighlightedSKUsForProductsUsingCollectionIds.toString(),
      payload: { productCodesToClean },
    });
  };

clearHighlightedSKUsForProductsUsingCollectionIds.toString = () =>
  'campaign-editor/clear-highlighted-skus-for-products-using-collection-ids';
